import React from 'react';
import { Control, actions } from 'react-redux-form';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import VehicleModelDiv from './vehicleModelDiv';
import {
  getFormData,
  getFormMetaController,
} from '../../../selectors/formSelector';
import FormApi from '../../../api/formApi';
import { getYear } from 'date-fns';
import * as logtypes from '../../../common/logtypes';
import { ltLogger, reportFieldEngagement } from '../../../bootstrap';
import DataLoader from '../../common/DataLoader';
import storageFactory from '../../../utils/storageFactory';
import {
  registerDefaultedField,
  clearDefaultedField,
} from '../../../actions/formTrackingActions';
import { fieldHasValue } from '../../../utils/misc';
import { bindKeyboardEvents } from '../../../common/keyboardHandlers';
import { reportErrorLogs } from '../../../utils/reportErrorLogs';
/*eslint-disable react/no-multi-comp,react/no-danger*/

const storage = storageFactory();

const SelectedWrapper = (props) => {
  let inputProps = Object.assign({}, props);
  delete inputProps.children;
  delete inputProps.submitted;
  delete inputProps.touched;
  delete inputProps.modelValue;

  const isSelected =
    props.autoFocus &&
    (props.submitted || (props.modelValue === props.htmlFor && props.touched));

  return (
    <label
      htmlFor={props.htmlFor}
      tabIndex="0"
      className={isSelected ? 'selected' : null}
      onKeyDown={bindKeyboardEvents(props.onClick)}
    >
      <input {...inputProps} />
      {props.children}
    </label>
  );
};

SelectedWrapper.propTypes = {
  htmlFor: PropTypes.number,
  autoFocus: PropTypes.bool,
  submitted: PropTypes.bool,
  children: PropTypes.object,
  modelValue: PropTypes.number,
  touched: PropTypes.bool,
};

class VehicleModel extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      vehicleModels: [],
      vehicleYears: [],
      isDataLoaded: false,
      clearedDefaultedField: false,
      defaultYearRegistered: false,
      shouldShowChooseYearPlaceholder: false,
    };
  }

  componentDidMount() {
    if (this.props.formData && this.props.formMeta) {
      const selectedLoanSubType = this.props.formMeta.AutomobileLoanSubType;
      const selectedMakeId = this.props.formData.VehicleMakeId;
      const selectedYear = this.props.formData.VehicleYear || null;

      // PHX-7000 :: INFO
      // Similar to the VehicleMake control, the VehicleModel control
      // collects multiple pieces of data (year and model)
      // However, we always fire engagement events for the model piece, so year
      // is the only thing I've added tracking for here
      // And on initial load, we don't have this value until fetchVehicleYears resolves
      if (fieldHasValue(selectedYear)) {
        this.props.dispatch(
          registerDefaultedField('VehicleYear', selectedYear)
        );
        this.setState({ defaultYearRegistered: true });
      }

      let appendDefaultVehicleYear =
        this.props?.step?.appenddefaultvehicleyear ?? false;
      this.fetchVehicleYears(
        selectedLoanSubType,
        selectedMakeId,
        selectedYear,
        appendDefaultVehicleYear
      );
    }
  }

  getVehicleCondition = (selectedLoanSubType) => {
    switch (selectedLoanSubType) {
      case 'NewCarPurchase':
        return 'new';
      case 'UsedCarPurchase':
      case 'REFINANCE':
      case 'BUYOUTLEASE':
        return 'used';
      default:
        return null;
    }
  };

  fetchVehicleYears = (
    selectedLoanSubType,
    selectedMakeId,
    selectedYear,
    appendDefaultVehicleYear
  ) => {
    let currentComponent = this;
    let vehicleCondition = this.getVehicleCondition(selectedLoanSubType);
    let automobileLoanType = this.props?.formData?.AutomobileLoanType;
    const shouldAppendChooseYearOption =
      appendDefaultVehicleYear &&
      (automobileLoanType === 'REFINANCE' ||
        automobileLoanType === 'BUYOUTLEASE');
    return FormApi.fetchVehicleYears(
      vehicleCondition,
      selectedMakeId,
      storage.getItem('lt_sessionKey')
    )
      .then((response) => {
        if (response && response.response && response.response.error) {
          ltLogger(logtypes.ERROR, undefined, {}, undefined, {
            error: new Error(response.response.error),
          });
          return null;
        }
        let vehicleYears = response != null ? response : [];
        if (vehicleYears != null) {
          const currentYear = getYear(new Date());
          let currentYearIndex = vehicleYears.findIndex(
            (item) => item === currentYear
          );
          let twoYearsBack = vehicleYears.findIndex(
            (item) => item === currentYear - 2
          );

          if (
            selectedYear === null ||
            (selectedYear && !vehicleYears.includes(selectedYear))
          ) {
            const getLatestVehicleYear = () => {
              return [...vehicleYears].sort((a, b) => b - a)[0];
            };

            if (vehicleCondition === 'new') {
              selectedYear =
                vehicleYears[currentYearIndex] || getLatestVehicleYear();
            } else {
              // PHX-7271 :: INFO :: Zach James
              // The "Choose Year" option mentioned here is talking about the
              // select placeholder option that literally says "Choose Year"
              // This was previously added as an actual value, which caused validation
              // issues
              // It now has a null value and should act only as a placeholder
              selectedYear = shouldAppendChooseYearOption
                ? null
                : vehicleYears[twoYearsBack] || getLatestVehicleYear();
            }
          }

          if (selectedYear !== null) {
            this.props.dispatch(
              actions.change('formData.VehicleYear', selectedYear)
            );
            if (!this.state.defaultYearRegistered) {
              this.props.dispatch(
                registerDefaultedField('VehicleYear', selectedYear)
              );
              this.setState({ defaultYearRegistered: true });
            }
            this.props.dispatch(actions.setTouched('formData.VehicleYear'));
          }
        }

        currentComponent.setState({
          vehicleYears:
            vehicleCondition === 'new'
              ? vehicleYears.slice(0, 2)
              : vehicleYears,
          shouldShowChooseYearPlaceholder: shouldAppendChooseYearOption,
          isDataLoaded: true, // Mark data as loaded
        });

        if (
          this.props.formData.VehicleYear !== undefined &&
          this.props.formData.VehicleYear !== null
        ) {
          this.fetchVehicleModels(
            selectedMakeId,
            selectedLoanSubType,
            this.props.formData.VehicleYear
          );
        }
      })
      .catch((error) => {
        ltLogger(logtypes.ERROR, undefined, {}, undefined, { error: error });
      });
  };

  fetchVehicleModels = (selectedMakeId, selectedLoanSubType, selectedYear) => {
    let currentComponent = this;
    let vehicleCondition = this.getVehicleCondition(selectedLoanSubType);
    currentComponent.setState({ isDataLoaded: false });

    // PHX-7148: Log VehicleModel fetch parameters
    const baseLogData = {
      selectedMakeId,
      selectedLoanSubType,
      selectedYear,
      vehicleCondition,
      userAgent: navigator.userAgent,
      isAndroid: /Android/i.test(navigator.userAgent),
      formId: this.props.formMeta?.formId,
      formUuid: this.props.formMeta?.formUuid,
      clickSessionId: this.props.formMeta?.clickSessionId,
      currentFormData: {
        VehicleMake: this.props.formData?.VehicleMake,
        VehicleMakeId: this.props.formData?.VehicleMakeId,
        VehicleYear: this.props.formData?.VehicleYear,
      },
    };

    if (
      selectedYear === 'Choose Year' ||
      selectedYear === null ||
      selectedYear === ''
    ) {
      reportErrorLogs(
        'VehicleModel fetch skipped - No valid year selected',
        baseLogData
      );
      currentComponent.setState({
        vehicleModels: [],
        isDataLoaded: true,
      });
      return;
    }

    reportErrorLogs('VehicleModel fetch started', baseLogData);

    return FormApi.fetchVehicleModels(
      selectedMakeId,
      vehicleCondition,
      selectedYear,
      storage.getItem('lt_sessionKey')
    )
      .then((response) => {
        if (response && response.response && response.response.error) {
          reportErrorLogs('VehicleModel API returned error', {
            ...baseLogData,
            apiError: response.response.error,
            responseData: response,
          });
          ltLogger(logtypes.ERROR, undefined, {}, undefined, {
            error: new Error(response.response.error),
          });
          currentComponent.setState({ vehicleModels: [], isDataLoaded: true });
          return null;
        }
        let modelCollection = response !== undefined ? response : [];

        // PHX-7148: Log VehicleModel fetch result
        reportErrorLogs('VehicleModel fetch completed', {
          ...baseLogData,
          modelsCount: modelCollection?.length,
        });

        currentComponent.setState({
          vehicleModels: modelCollection,
          isDataLoaded: true,
        });
      })
      .catch((error) => {
        // PHX-7148: Log VehicleModel fetch failure
        reportErrorLogs('VehicleModel fetch failed', {
          ...baseLogData,
          error: error?.message,
          errorStack: error?.stack,
        });
        currentComponent.setState({ isDataLoaded: true });
        ltLogger(logtypes.ERROR, undefined, {}, undefined, { error: error });
      });
  };

  render() {
    const { isDataLoaded } = this.state;
    return !isDataLoaded ? (
      <DataLoader />
    ) : (
      <div>
        {this.props.formMeta.AutomobileLoanSubType === 'NewCarPurchase' ? (
          <div aria-label="Input Container for Vehicle Year">
            {this.state.vehicleYears.map((item) => (
              <div
                key={`newVehicleYear-${item}`}
                className={`ltFormRadioControl ltFormRadioControl${item}`}
              >
                <Control.radio
                  name={item}
                  id={item}
                  model={`formData.VehicleYear`}
                  className={`ltFormRadioButton ${item}`}
                  component={SelectedWrapper}
                  value={item}
                  changeAction={(model, value, event) => (dispatch) => {
                    if (event && value !== event.currentValue) {
                      return;
                    }
                    dispatch(actions.change(model, value));
                    if (
                      event &&
                      this.props.steps.length === 1 &&
                      event.currentValue === value
                    ) {
                      this.fetchVehicleModels(
                        this.props.formData.VehicleMakeId,
                        this.props.formMeta.AutomobileLoanSubType,
                        value
                      );
                    }
                  }}
                  mapProps={{
                    autoFocus: (props) => props.modelValue === item,
                    htmlFor: item,
                    submitted: ({ fieldValue }) => fieldValue.submitted,
                    modelValue: (props) => props.modelValue,
                    touched: ({ fieldValue }) => fieldValue.touched,
                    onClick: (props) => () => {
                      if (props.value === props.controlProps.value) {
                        reportFieldEngagement('VehicleYear', props.value);
                        if (!this.state.clearedDefaultedField) {
                          props.dispatch(clearDefaultedField('VehicleYear'));
                        }
                        props.dispatch(actions.setTouched(props.model));
                        props.dispatch(
                          props.changeAction(props.model, props.value, {
                            currentValue: props.controlProps.value,
                          })
                        );
                      }
                    },
                  }}
                >
                  <div dangerouslySetInnerHTML={{ __html: item }} />
                </Control.radio>
              </div>
            ))}
          </div>
        ) : (
          <div
            className={'ltFormControlSelect'}
            aria-label="Input for Vehicle Year"
          >
            <Control.select
              model={`formData.VehicleYear`}
              className="ltFormControl"
              updateOn={['change']}
              changeAction={(model, value, event) => (dispatch) => {
                if (value === '' || value === 'Choose Year' || !value) {
                  return;
                }
                const numericValue = parseInt(value, 10);
                reportFieldEngagement('VehicleYear', numericValue);
                dispatch(actions.change(model, numericValue));
                this.setState({ selectedVehicleYear: numericValue });
                if (event && this.props.steps.length === 1) {
                  this.fetchVehicleModels(
                    this.props.formData.VehicleMakeId,
                    this.props.formMeta.AutomobileLoanSubType,
                    numericValue
                  );
                }
              }}
              mapProps={{
                value: (props) => {
                  // PHX-7271 :: INFO :: Zach James
                  // If we're showing the placeholder and no value is set, return empty string
                  if (
                    this.state.shouldShowChooseYearPlaceholder &&
                    !props.viewValue
                  ) {
                    return '';
                  }
                  return props.viewValue ? props.viewValue : '';
                },
              }}
            >
              {this.state.shouldShowChooseYearPlaceholder && (
                <option value="" disabled>
                  Choose Year
                </option>
              )}
              {this.state.vehicleYears.map((item) => (
                <option key={`usedVehicleYear-${item}`} value={item}>
                  {item}
                </option>
              ))}
            </Control.select>
          </div>
        )}
        <VehicleModelDiv
          fetchedVehicleModels={this.state.vehicleModels}
          selectedVehicleYear={this.state.selectedVehicleYear}
          handleNext={this.props.handleNext}
          parentProps={this.props}
        />
      </div>
    );
  }
}

/*eslint-enable */
VehicleModel.propTypes = {
  step: PropTypes.object,
  steps: PropTypes.array,
  handleNext: PropTypes.func,
  fieldValue: PropTypes.object,
  modelValue: PropTypes.object,
  controlProps: PropTypes.object,
  viewValue: PropTypes.string,
  value: PropTypes.string,
  dispatch: PropTypes.func,
  model: PropTypes.object,
  changeAction: PropTypes.func,
  formData: PropTypes.object,
  formMeta: PropTypes.object,
};

function mapStateToProps(state) {
  return {
    formData: getFormData(state),
    formMeta: getFormMetaController(state),
  };
}

export default connect(mapStateToProps)(VehicleModel);
